/**
 * Zoom Compensation Utilities for MUI Components
 * 
 * When CSS zoom is applied to the body, MUI's Popover/Menu positioning becomes
 * incorrect because getBoundingClientRect() returns zoomed coordinates, but
 * these coordinates get double-affected when applied as CSS positions inside
 * the zoomed body.
 * 
 * Solution: Apply inverse zoom (1/zoomFactor) to portal containers. This:
 * 1. Cancels out the body zoom for the portal
 * 2. Makes positioning work correctly (since the element is effectively unzoomed)
 * 3. However, makes the dropdown appear at base size (not affected by browser zoom)
 * 
 * Note: Dropdowns will appear at "100% zoom" size regardless of browser zoom.
 * This is an acceptable trade-off for correct positioning.
 */

/**
 * Gets the current zoom factor based on browser detection.
 * Must match the CSS zoom values in index.css.
 */
export const getZoomFactor = (): number => {
    return 1; // Forced to 1 for testing
    if (typeof navigator === 'undefined' || typeof document === 'undefined') {
        return 1;
    }

    // Check for browser class on html element (set in index.tsx)
    const htmlElement = document.documentElement;
    if (htmlElement.classList.contains('browser-edge')) {
        return 1.05;
    }
    if (htmlElement.classList.contains('browser-chrome')) {
        return 0.95;
    }

    return 1;
};

/**
 * Gets the inverse zoom factor for compensating body zoom.
 */
export const getInverseZoomFactor = (): number => {
    const zoomFactor = getZoomFactor();
    return zoomFactor === 1 ? 1 : 1 / zoomFactor;
};

/**
 * Get slotProps for MUI Menu component with zoom compensation.
 * Applies inverse zoom to the Menu root to cancel out body zoom.
 * 
 * @example
 * import { getZoomCompensatedMenuSlotProps } from '../utils/zoomCompensation';
 * 
 * <Menu
 *   slotProps={getZoomCompensatedMenuSlotProps()}
 * >
 */
export const getZoomCompensatedMenuSlotProps = (existingSlotProps: object = {}) => {
    const inverseZoom = getInverseZoomFactor();

    if (inverseZoom === 1) {
        return existingSlotProps;
    }

    return {
        ...existingSlotProps,
        root: {
            sx: {
                zoom: inverseZoom,
            }
        }
    };
};

/**
 * Get MenuProps for MUI Select component with zoom compensation.
 * Applies inverse zoom to cancel out body zoom on the dropdown.
 * 
 * @param existingProps Existing MenuProps to merge with
 * @example
 * import { getZoomCompensatedSelectMenuProps } from '../utils/zoomCompensation';
 * 
 * <Select
 *   MenuProps={getZoomCompensatedSelectMenuProps({ sx: { zIndex: 1400 } })}
 * >
 */
export const getZoomCompensatedSelectMenuProps = (existingProps: Record<string, unknown> = {}) => {
    const inverseZoom = getInverseZoomFactor();

    if (inverseZoom === 1) {
        return existingProps;
    }

    return {
        ...existingProps,
        sx: {
            ...(typeof existingProps.sx === 'object' ? existingProps.sx : {}),
            zoom: inverseZoom,
        }
    };
};

/**
 * Get sx prop for Menu/Popover root with zoom compensation.
 * Use when you need to add zoom compensation to an existing sx prop.
 * 
 * @param existingSx Existing sx object to merge with
 * @example
 * <Menu
 *   sx={getZoomCompensatedSx({ maxHeight: 400 })}
 * >
 */
export const getZoomCompensatedSx = (existingSx: object = {}) => {
    const inverseZoom = getInverseZoomFactor();

    if (inverseZoom === 1) {
        return existingSx;
    }

    return {
        ...existingSx,
        zoom: inverseZoom,
    };
};

/**
 * Get slotProps for MUI Autocomplete component with zoom compensation.
 * Applies inverse zoom to the Popper to cancel out body zoom.
 * 
 * @param existingProps Existing slotProps to merge with (optional)
 * @example
 * import { getZoomCompensatedAutocompleteSlotProps } from '../utils/zoomCompensation';
 * 
 * <Autocomplete
 *   slotProps={getZoomCompensatedAutocompleteSlotProps()}
 * >
 */
export const getZoomCompensatedAutocompleteSlotProps = (existingProps: Record<string, any> = {}) => {
    const inverseZoom = getInverseZoomFactor();

    if (inverseZoom === 1) {
        return existingProps;
    }

    return {
        ...existingProps,
        popper: {
            ...(existingProps.popper || {}),
            sx: {
                ...(existingProps.popper?.sx || {}),
                zoom: inverseZoom,
            }
        }
    };
};
